/**
 * BulkProgressTracker - Real-time Bulk Operation Progress Tracking
 *
 * FERP CRITICAL: Real-time progress tracking with polling
 *
 * Features:
 * - Real-time progress bar (polls every 1 second)
 * - Success/failure counters with badges
 * - Processing speed estimate
 * - Cancel button (if still processing)
 * - Status badges with colors
 * - Completion alerts
 *
 * Architecture Principles:
 * - Data-driven (status from BulkOperationRepository)
 * - Real-time polling (1000ms interval)
 * - Repository pattern
 * - Immutable audit trails
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Progress,
  Badge,
  Group,
  Button,
  Alert,
  Card,
  Loader,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertCircle,
  IconClock,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  BulkOperation,
  BulkOperationRepository,
  BatchStatus,
} from '../data/repositories/BulkOperationRepository';

export interface BulkProgressTrackerProps {
  /** Batch ID to track */
  batchId: string;
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Callback when operation completes */
  onComplete?: (operation: BulkOperation) => void;
  /** Callback when cancel is requested */
  onCancel?: (batchId: string) => void;
}

/**
 * Get status color
 */
function getStatusColor(status: BatchStatus): string {
  switch (status) {
    case BatchStatus.PENDING:
      return 'gray';
    case BatchStatus.PROCESSING:
      return 'blue';
    case BatchStatus.COMPLETED:
      return 'green';
    case BatchStatus.FAILED:
      return 'red';
    case BatchStatus.CANCELLED:
      return 'orange';
    default:
      return 'gray';
  }
}

/**
 * Calculate processing speed (rows/sec)
 */
function calculateSpeed(
  processedRows: number,
  startedAt: string,
  completedAt?: string
): number {
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationSeconds = (end - start) / 1000;

  if (durationSeconds === 0) return 0;
  return Math.round(processedRows / durationSeconds);
}

/**
 * Bulk Progress Tracker
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (status from BulkOperationRepository)
 * - ✅ Real-time polling (1000ms interval)
 * - ✅ Repository pattern
 * - ✅ Immutable audit trails
 */
export function BulkProgressTracker({
  batchId,
  tenantId,
  onComplete,
  onCancel,
}: BulkProgressTrackerProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(() => new BulkOperationRepository(apolloClient), [apolloClient]);

  // State
  const [operation, setOperation] = useState<BulkOperation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(true);

  /**
   * Fetch operation status
   */
  const fetchStatus = useCallback(async () => {
    try {
      const result = await repository.getBulkOperationStatus(batchId, tenantId);
      if (result) {
        setOperation(result);
        setLoading(false);

        // Stop polling if completed/failed/cancelled
        if (
          result.status === BatchStatus.COMPLETED ||
          result.status === BatchStatus.FAILED ||
          result.status === BatchStatus.CANCELLED
        ) {
          setPollingEnabled(false);
          if (onComplete) {
            onComplete(result);
          }
        }
      }
    } catch (err) {
      console.error('[BulkProgressTracker] Failed to fetch status:', err);
      setError(err as Error);
      setLoading(false);
      setPollingEnabled(false);
    }
  }, [batchId, tenantId, repository, onComplete]);

  /**
   * Poll for status updates
   */
  useEffect(() => {
    // Initial fetch
    fetchStatus();

    // Setup polling
    let intervalId: NodeJS.Timeout | null = null;
    if (pollingEnabled) {
      intervalId = setInterval(fetchStatus, 1000); // Poll every 1 second
    }

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchStatus, pollingEnabled]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(batchId);
    }
  }, [batchId, onCancel]);

  // Loading state
  if (loading) {
    return (
      <Card withBorder>
        <Group justify="center" p="xl">
          <Loader size="lg" />
          <Text>Loading operation status...</Text>
        </Group>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert color="red" title="Error" icon={<IconAlertCircle />}>
        Failed to load operation status: {error.message}
      </Alert>
    );
  }

  // No operation found
  if (!operation) {
    return (
      <Alert color="yellow" title="Not Found" icon={<IconAlertCircle />}>
        Bulk operation not found (batchId: {batchId})
      </Alert>
    );
  }

  // Calculate progress
  const processedRows = operation.successCount + operation.failureCount;
  const percentage = operation.totalRows > 0
    ? Math.round((processedRows / operation.totalRows) * 100)
    : 0;
  const remainingRows = operation.totalRows - processedRows;
  const speed = calculateSpeed(processedRows, operation.startedAt, operation.completedAt);
  const successRate = processedRows > 0
    ? Math.round((operation.successCount / processedRows) * 100)
    : 0;

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" ta="center">
            <Title order={4}>Bulk Operation Progress</Title>
            <Badge
              size="lg"
              color={getStatusColor(operation.status)}
              leftSection={
                operation.status === BatchStatus.COMPLETED ? (
                  <IconCheck size={14} />
                ) : operation.status === BatchStatus.FAILED ? (
                  <IconX size={14} />
                ) : operation.status === BatchStatus.PROCESSING ? (
                  <IconClock size={14} />
                ) : null
              }
            >
              {operation.status}
            </Badge>
          </Group>

          <div>
            <Text size="sm" c="dimmed" mb="xs">
              File: {operation.filename}
            </Text>
            <Progress
              value={percentage}
              size="xl"
              striped={operation.status === BatchStatus.PROCESSING}
              animated={operation.status === BatchStatus.PROCESSING}
              color={
                operation.status === BatchStatus.COMPLETED
                  ? 'green'
                  : operation.status === BatchStatus.FAILED
                  ? 'red'
                  : 'blue'
              }
            />
            <Group justify="space-between" mt="xs">
              <Text size="sm" c="dimmed">
                {processedRows} / {operation.totalRows} rows processed
              </Text>
              <Text size="sm" fw={600}>
                {percentage}%
              </Text>
            </Group>
          </div>

          <Group grow>
            <Card bg="green.0" p="xs">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Success
                </Text>
                <Text size="xl" fw={700} c="green" ta="center">
                  {operation.successCount}
                </Text>
              </Stack>
            </Card>

            <Card bg="red.0" p="xs">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Failed
                </Text>
                <Text size="xl" fw={700} c="red" ta="center">
                  {operation.failureCount}
                </Text>
              </Stack>
            </Card>

            <Card bg="gray.0" p="xs">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Remaining
                </Text>
                <Text size="xl" fw={700} ta="center">
                  {remainingRows}
                </Text>
              </Stack>
            </Card>
          </Group>

          {processedRows > 0 && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Success rate: {successRate}%
              </Text>
              <Text size="sm" c="dimmed">
                Speed: ~{speed} rows/sec
              </Text>
            </Group>
          )}

          {operation.status === BatchStatus.PROCESSING && onCancel && (
            <Button color="red" onClick={handleCancel} variant="light">
              Cancel Operation
            </Button>
          )}
        </Stack>
      </Card>

      {operation.status === BatchStatus.COMPLETED && (
        <Alert color="green" title="Processing Complete" icon={<IconCheck />}>
          Successfully processed {operation.successCount} of {operation.totalRows} rows
          {operation.failureCount > 0 && ` (${operation.failureCount} failed)`}.
        </Alert>
      )}

      {operation.status === BatchStatus.FAILED && (
        <Alert color="red" title="Processing Failed" icon={<IconX />}>
          {operation.errorSummary || 'Operation failed due to errors.'}
        </Alert>
      )}

      {operation.status === BatchStatus.CANCELLED && (
        <Alert color="orange" title="Operation Cancelled" icon={<IconAlertCircle />}>
          Bulk operation was cancelled. Processed {processedRows} of {operation.totalRows} rows
          before cancellation.
        </Alert>
      )}
    </Stack>
  );
}

export default BulkProgressTracker;
